import { useState, useEffect, useMemo, useCallback } from 'react';
import { AnimatePresence, m as motion } from 'framer-motion';
import { analytics } from '@eti/utils';
import { useDirection, useProperty, useTranslation } from '@eti/providers';
import { css, cx } from '@eti/styles';
import { Heading, Inline, vars, breakpoints, LinkButton } from '@etg/wings';
import { useMediaQuery } from 'usehooks-ts';
import { SEAT_MAP } from '../../products';
import { flatBounds, getOriginDestinationCities, getSegment, getSegmentIndex, getSeatCharacteristics, isSeatPreferenceSegment } from '../utils/segmentUtils';
import { getSegmentTravelers, getNonBookedTravelerSeats } from '../utils/travelerUtils';
import { focusOnActiveSeatMapTravelerButton } from '../utils/focusUtils';
import { Direction, useSeatMapNavigation } from '../hooks/useSeatMapNavigation';
import useSeatMapForm from '../hooks/useSeatMapForm';
import SeatMapUnbookedSegmentsDialog from './SeatMapUnbookedSegmentsDialog';
import SeatMapHeader from './SeatMapHeader';
import SeatMapFooter from './SeatMapFooter';
import SeatingLightOrBeside from './SeatingLightOrBeside';
import SeatMapTravelers from './travelers/SeatMapTravelers';
import SeatCharacteristics from './SeatCharacteristics';
import SegmentPanel from './SegmentPanel';
import SeatMapPersistDialog from './SeatMapPersistDialog';
import { ConditionalWrapper } from '@eti/components';
import { modalTypes, modalRedirectDestinations, modalActionTypes } from '../constants/modal';
import { slideDirections } from '../constants/slideDirections';
import SeatSummary from './summary/SeatSummary';
import SeatMapCabinSection from './SeatMapCabinSection';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export { Direction };
const containerStyle = css`
  border-radius: ${vars.card.borderRadius};
  display: flex;
  flex: 1;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`;
const headingInlineStyles = css`
  align-items: center;
  justify-content: space-between;
`;
const headingInlinePaddingStyles = css`
  padding: 16px;
`;
const contentWrapperStyles = css`
  margin-bottom: 16px;
  overflow-x: clip;

  @media (min-width: ${breakpoints._768}) {
    border-radius: ${vars.card.borderRadius};
    box-shadow: 0 2px 12px rgb(0 0 0 / 5%);
    margin-bottom: 40px;
    overflow: clip;
  }
`;
const animatedContentStyles = {
  base: css`
    padding: 0 16px 56px;
  `,
  centered: css`
    display: flex;
    flex-direction: column;

    & > :first-child {
      margin-block-start: auto;
    }

    & > :last-child {
      margin-block-end: auto;
    }
  `,
  fullHeight: css`
    height: 100%;
  `,
  minHeight: css`
    min-height: calc(100vh - 413px);

    @media (min-width: ${breakpoints._768}) {
      min-height: calc(100vh - 401px);
    }

    @media (min-width: ${breakpoints._1440}) {
      min-height: calc(100vh - 320px);
    }
  `,
  pageBackground: css`
    background-color: ${vars.colors.page.background.primary};
  `,
  scrollable: css`
    overflow-y: auto;
  `,
  surfaceBackground: css`
    background-color: ${vars.colors.surface.primary};
  `
};
const flipSlideDirection = direction => direction === slideDirections.FORWARD ? slideDirections.BACKWARD : slideDirections.FORWARD;
const slideVariants = {
  enter: direction => ({
    opacity: 0,
    x: direction === slideDirections.FORWARD ? '100%' : '-100%'
  }),
  center: {
    opacity: 1,
    x: 0
  },
  exit: direction => ({
    opacity: 0,
    x: direction === slideDirections.FORWARD ? '-100%' : '100%'
  })
};
const SeatMap = ({
  bounds = [],
  form,
  isPostBooking = false,
  onCallback,
  onConfirm,
  onDismiss,
  shouldRenderInline,
  travelers
}) => {
  var _nonSelectedAvailable, _nonSelectedAvailable2, _currentSegment$carri;
  const {
    isRTL
  } = useDirection();
  const {
    p
  } = useProperty();
  const {
    t
  } = useTranslation();
  const isLaptopOrAboveViewport = useMediaQuery(`(min-width: ${breakpoints._1170})`);
  const isManualNavigationEnabled = p('IbeClient.SeatMap.Segment.Navigation.Manual.Enabled');
  const usePersuasionConfiguration = p('IbeClient.TravelerDetails.OptionalProductsRequireValidation.Enabled');
  const ryanairCarrierCode = p('IbeClient.RyanairMarketingCarriers');
  const isSkipButtonEnabled = p('IbeClient.SeatMap.SkipLinkButton.Enabled');
  const isSummarySegmentEnabledForAllCarriers = p('IbeClient.SeatMap.SummarySegment.Enabled');
  const isSummarySegmentEnabledForCarriers = p('IbeClient.SeatMap.SummarySegment.Enabled.Carriers');
  const {
    deckSelectionsBySegment,
    getSelected,
    isSummaryShowing,
    onChange,
    onChangeSeatingLightBeside,
    onClose,
    onDeckSelectionChange,
    onOptOut,
    onRemove,
    onSeatConfirmation,
    selectedSeats,
    setIsSummaryShowing,
    shouldShowConfirmationModal,
    totalMarkup,
    totalPrice
  } = useSeatMapForm({
    form,
    onConfirm,
    onDismiss,
    shouldRenderInline,
    travelers
  });
  const [modalType, setModalType] = useState(modalTypes.SEAT_MAP_PERSIST_IN_PRODUCT_UNBOOKED_SEGMENTS);
  const [modalRedirectTo, setModalRedirectTo] = useState(modalRedirectDestinations.DEFAULT);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isSkipButtonFocused, setIsSkipButtonFocused] = useState(false);
  const [isSkipButtonHovered, setIsSkipButtonHovered] = useState(false);
  const [expandedCharacteristicKey, setExpandedCharacteristicKey] = useState(null);
  const [travelerButtonWithRemoveLink, setTravelerButtonWithRemoveLink] = useState(undefined);
  const [slideDirection, setSlideDirection] = useState(slideDirections.FORWARD);
  const resolvedSlideDirection = isRTL ? flipSlideDirection(slideDirection) : slideDirection;
  const isPersistDialogOpen = isConfirmationOpen && modalType === modalTypes.SEAT_MAP_PERSIST_IN_PRODUCT;
  const isUnbookedSegmentsDialogOpen = isConfirmationOpen && modalType === modalTypes.SEAT_MAP_PERSIST_IN_PRODUCT_UNBOOKED_SEGMENTS;
  const flattenedBounds = useMemo(() => flatBounds(bounds), [bounds]);
  const headerCities = useMemo(() => getOriginDestinationCities(bounds), [bounds]);
  const isRyanairInTrip = useMemo(() => {
    const lowerCaseCodes = ryanairCarrierCode === null || ryanairCarrierCode === void 0 ? void 0 : ryanairCarrierCode.map(code => code.toLowerCase());
    return flattenedBounds.some(segment => {
      var _segment$carrier;
      return lowerCaseCodes === null || lowerCaseCodes === void 0 ? void 0 : lowerCaseCodes.includes((_segment$carrier = segment.carrier) === null || _segment$carrier === void 0 || (_segment$carrier = _segment$carrier.code) === null || _segment$carrier === void 0 ? void 0 : _segment$carrier.toLowerCase());
    });
  }, [flattenedBounds, ryanairCarrierCode]);
  const isSummarySegmentEnabledForCarrier = useMemo(() => {
    const lowerCaseCodes = isSummarySegmentEnabledForCarriers === null || isSummarySegmentEnabledForCarriers === void 0 ? void 0 : isSummarySegmentEnabledForCarriers.map(code => code.toLowerCase());
    return flattenedBounds.some(segment => {
      var _segment$carrier2;
      return lowerCaseCodes === null || lowerCaseCodes === void 0 ? void 0 : lowerCaseCodes.includes((_segment$carrier2 = segment.carrier) === null || _segment$carrier2 === void 0 || (_segment$carrier2 = _segment$carrier2.code) === null || _segment$carrier2 === void 0 ? void 0 : _segment$carrier2.toLowerCase());
    });
  }, [flattenedBounds, isSummarySegmentEnabledForCarriers]);
  const isSummarySegmentEnabled = isSummarySegmentEnabledForAllCarriers || isSummarySegmentEnabledForCarrier;
  const getUnbookedSegmentsDismissText = () => {
    if (!isSummarySegmentEnabled) {
      return t('Product.SeatMap.AllSeatsSelectedConfirmation.DismissText');
    }
    if (modalRedirectTo === modalRedirectDestinations.PAYMENT) {
      return t('Product.SeatMap.AllSeatsSelectedConfirmation.Payment.DismissText');
    }
    if (modalRedirectTo === modalRedirectDestinations.SUMMARY) {
      return t('Product.SeatMap.AllSeatsSelectedConfirmation.Summary.DismissText');
    }
    return t('Product.SeatMap.AllSeatsSelectedConfirmation.DismissText');
  };
  const unbookedSegmentsDismissText = getUnbookedSegmentsDismissText();
  const onShowConfirmationModal = (type, redirectTo) => {
    setModalType(type);
    setModalRedirectTo(redirectTo);
    setIsConfirmationOpen(true);
  };
  const closeConfirmationModal = () => {
    setIsConfirmationOpen(false);
  };
  const handleOnOptOut = () => {
    analytics.pushToDataLayer({
      event: 'seatingVisible',
      seatingSegment: 'NO'
    });
    onCallback === null || onCallback === void 0 || onCallback({});
    onOptOut === null || onOptOut === void 0 || onOptOut();
  };
  const handleOnClose = (data = selectedSeats) => {
    analytics.pushToDataLayer({
      event: 'seatingVisible',
      seatingSegment: 'BOOK'
    }, {
      event: 'seatingVisible',
      seatingOnBook: 'YES'
    });
    onCallback === null || onCallback === void 0 || onCallback(selectedSeats);
    onClose === null || onClose === void 0 || onClose(data);
  };
  const handleOnSeatConfirmation = data => {
    analytics.pushToDataLayer({
      event: 'seatingVisible',
      seatingSegment: 'BOOK'
    }, {
      event: 'seatingVisible',
      seatingOnBook: 'YES'
    });
    onCallback === null || onCallback === void 0 || onCallback(selectedSeats);
    onSeatConfirmation === null || onSeatConfirmation === void 0 || onSeatConfirmation(data);
  };
  const onSelectionComplete = () => {
    if (shouldRenderInline) {
      handleOnSeatConfirmation(selectedSeats);
      return;
    }
    if (totalPrice === 0 && isPostBooking) {
      handleOnOptOut();
      return;
    }
    handleOnClose(selectedSeats);
  };
  const {
    activeDeck,
    activeDeckType,
    activeSegment,
    activeTraveler,
    canTriggerUnbookedSegmentsDialog,
    deckOptions,
    foundSegment,
    fromSummary,
    handleChangeTraveler,
    handleDeckChange,
    handleExitComplete,
    handleOnNextSegmentClick,
    handleOnPreviousSegmentClick,
    handleToggleSeatSelect,
    isFirstSegment,
    isLastSegment,
    isSeatPreferenceAvailable,
    nonSelectedAvailableSegments,
    setFromSummary,
    totalSelectedSeats
  } = useSeatMapNavigation({
    deckSelectionsBySegment,
    flattenedBounds,
    getSelected,
    isManualNavigationEnabled,
    isSummarySegmentEnabled,
    isSummaryShowing,
    onDeckSelectionChange,
    onShowConfirmationModal,
    onSelectionComplete,
    selectedSeats,
    setIsSummaryShowing,
    t,
    travelers,
    usePersuasionConfiguration
  });
  const currentSegment = getSegment(flattenedBounds, activeSegment);
  const isSeatingPreferenceAvailable = isSeatPreferenceSegment(currentSegment);
  const __typename = currentSegment === null || currentSegment === void 0 ? void 0 : currentSegment.__typename;
  const price = currentSegment === null || currentSegment === void 0 ? void 0 : currentSegment.price;
  const seatCharacteristics = getSeatCharacteristics(flattenedBounds, activeSegment);
  const isSegmentUnavailable = !isSeatPreferenceAvailable && (!foundSegment || !foundSegment.isAvailable);
  const shouldShowSeatCharacteristicsButton = !(isSeatPreferenceAvailable || isSegmentUnavailable || isSummaryShowing);
  const isSeatSelectionDisabled = isSeatingPreferenceAvailable || isSegmentUnavailable;
  const isFinalSelectionStep = isSummaryShowing || isLastSegment && !isSummarySegmentEnabled;
  const isSeatSelectionAvailable = Boolean(!isSeatingPreferenceAvailable && (currentSegment === null || currentSegment === void 0 ? void 0 : currentSegment.isAvailable));
  const showSeatPickerSidebar = !isSummaryShowing && !isSeatSelectionDisabled && isSeatSelectionAvailable && isLaptopOrAboveViewport;
  const showMobileSeatPicker = !isSummaryShowing && isSeatSelectionAvailable && !isLaptopOrAboveViewport;
  const showCabinSection = !isSummaryShowing && isSeatSelectionAvailable;
  const showSeatingLightOrBeside = !isSummaryShowing && isSeatingPreferenceAvailable && !!price;
  const showUnavailableSegment = !isSummaryShowing && isSegmentUnavailable;
  const isSeatSelectionUnavailable = showSeatingLightOrBeside || showUnavailableSegment;
  const currentSegmentIndex = getSegmentIndex(flattenedBounds, activeSegment) + 1;
  const firstUnbookedSegmentIndex = (_nonSelectedAvailable = (_nonSelectedAvailable2 = nonSelectedAvailableSegments[0]) === null || _nonSelectedAvailable2 === void 0 ? void 0 : _nonSelectedAvailable2.boundSegmentIndex) !== null && _nonSelectedAvailable !== void 0 ? _nonSelectedAvailable : null;
  const carrierNameLowerCase = currentSegment === null || currentSegment === void 0 || (_currentSegment$carri = currentSegment.carrier) === null || _currentSegment$carri === void 0 || (_currentSegment$carri = _currentSegment$carri.name) === null || _currentSegment$carri === void 0 ? void 0 : _currentSegment$carri.toLowerCase().replace(/[^a-z0-9]+(.)/g, (_, c) => c.toUpperCase());
  const selectedSeatsBySegment = useMemo(() => Object.fromEntries(flattenedBounds.map(bound => [bound.boundSegmentIndex, getSelected(bound.boundSegmentIndex)])), [flattenedBounds, getSelected]);
  const cabins = useMemo(() => {
    var _currentSegment$activ, _currentSegment$activ2;
    if (!currentSegment || !activeDeck) {
      return [];
    }
    return (_currentSegment$activ = (_currentSegment$activ2 = currentSegment[activeDeck.value]) === null || _currentSegment$activ2 === void 0 ? void 0 : _currentSegment$activ2.cabins) !== null && _currentSegment$activ !== void 0 ? _currentSegment$activ : [];
  }, [currentSegment, activeDeck]);
  const handleChangeSeat = useCallback(({
    carrier,
    column,
    price,
    row,
    seat,
    markup
  }) => {
    const selected = getSelected(activeSegment);
    const existingSeat = selected.find(s => s.value === seat);
    if (existingSeat) {
      return;
    }
    if (fromSummary) {
      setFromSummary(false);
    }
    onChange(activeSegment, activeTraveler, carrier, column, row, seat, price, markup, activeDeckType);
  }, [activeSegment, activeTraveler, activeDeckType, fromSummary, getSelected, onChange, setFromSummary]);
  useEffect(() => {
    analytics.pushToDataLayer({
      event: 'seatingVisible',
      seatingCurrentSegment: activeSegment
    });
  }, [activeSegment]);
  analytics.useLogMouseFlowTagEffect('Seatmap 4.0');
  const handleCloseButton = () => {
    if (usePersuasionConfiguration && shouldShowConfirmationModal && p('IbeClient.TravelerDetails.Modal') === 'SEATMAP') {
      onShowConfirmationModal(modalTypes.SEAT_MAP_PERSIST_IN_PRODUCT, modalRedirectDestinations.PAYMENT);
    } else if (shouldRenderInline) {
      handleOnOptOut();
      onConfirm === null || onConfirm === void 0 || onConfirm();
    } else {
      handleOnOptOut();
    }
  };
  const handleSelectSeatFromSummary = (boundSegmentIndex, traveler, deckType) => {
    handleToggleSeatSelect(boundSegmentIndex, traveler, deckType);
    setTravelerButtonWithRemoveLink(traveler);
  };
  const handleConfirmationSelection = (action, segment) => {
    closeConfirmationModal();
    if (action !== modalActionTypes.SEAT_MAP_PERSIST_ACTION_DISMISS) {
      if (!segment) {
        return;
      }
      const nonBookedTravelerSeats = getNonBookedTravelerSeats(flattenedBounds.find(seg => seg.boundSegmentIndex === segment), totalSelectedSeats);
      handleSelectSeatFromSummary(segment, nonBookedTravelerSeats[0]);
      requestAnimationFrame(() => {
        focusOnActiveSeatMapTravelerButton(nonBookedTravelerSeats[0]);
      });
      return;
    }
    if (shouldRenderInline && modalRedirectTo !== modalRedirectDestinations.SUMMARY) {
      handleOnOptOut();
      onConfirm === null || onConfirm === void 0 || onConfirm();
      return;
    }
    if (totalPrice === 0 && isPostBooking) {
      handleOnOptOut();
      return;
    }
    if (modalRedirectTo !== modalRedirectDestinations.SUMMARY) {
      handleOnOptOut();
      return;
    }
    if (!isSummarySegmentEnabled) {
      onSelectionComplete();
    } else {
      setIsSummaryShowing(true);
    }
  };
  const handleToggle = key => {
    setExpandedCharacteristicKey(prev => prev === key ? null : key);
  };
  const handleNextSegment = () => {
    setSlideDirection(slideDirections.FORWARD);
    handleOnNextSegmentClick();
  };
  const handlePreviousSegment = () => {
    setSlideDirection(slideDirections.BACKWARD);
    handleOnPreviousSegmentClick();
  };
  return /*#__PURE__*/_jsxs("div", {
    className: cx({
      [containerStyle]: !shouldRenderInline
    }),
    "data-offer-rate": SEAT_MAP.id,
    "data-testid": "seatMap-maincontent",
    children: [shouldRenderInline && /*#__PURE__*/_jsx(ConditionalWrapper, {
      condition: isSkipButtonEnabled,
      wrapper: children => /*#__PURE__*/_jsxs(Inline, {
        className: headingInlineStyles,
        children: [children, /*#__PURE__*/_jsx(LinkButton, {
          className: headingInlinePaddingStyles,
          "data-testid": "seatMap-skipButton",
          onClick: handleCloseButton,
          children: t('Product.SeatMap.Skip.LinkButton.Text')
        })]
      }),
      children: /*#__PURE__*/_jsx(Heading, {
        className: headingInlinePaddingStyles,
        level: 1,
        children: t('Product.SeatMap.Header.Label')
      })
    }), /*#__PURE__*/_jsx(ConditionalWrapper, {
      condition: shouldRenderInline,
      wrapper: children => /*#__PURE__*/_jsx("div", {
        className: contentWrapperStyles,
        children: children
      }),
      children: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(SeatMapHeader, {
          activeSeatAssignments: getSelected(activeSegment),
          activeSegment: activeSegment,
          activeTraveler: activeTraveler,
          currentSegmentData: currentSegment,
          currentSegmentIndex: currentSegmentIndex,
          from: headerCities.from,
          handleCloseButton: handleCloseButton,
          isMobileSelectionHidden: !showMobileSeatPicker,
          isPersistDialogOpen: isPersistDialogOpen,
          isRyanairInTrip: isRyanairInTrip,
          isSegmentCountHidden: isSummaryShowing,
          isUpperPartHidden: shouldRenderInline,
          onChange: handleChangeTraveler,
          onRemoveSeat: onRemove,
          seatTravelers: getSegmentTravelers(travelers, flattenedBounds, activeSegment),
          setTravelerButtonWithRemoveLink: setTravelerButtonWithRemoveLink,
          shouldRenderInline: shouldRenderInline,
          to: headerCities.to,
          totalSegmentsCount: flattenedBounds.length,
          travelerButtonWithRemoveLink: travelerButtonWithRemoveLink
        }), /*#__PURE__*/_jsx(AnimatePresence, {
          custom: resolvedSlideDirection,
          initial: false,
          exitBeforeEnter: true,
          onExitComplete: handleExitComplete,
          children: /*#__PURE__*/_jsxs(motion.div, {
            animate: "center",
            className: cx(animatedContentStyles.base, {
              [animatedContentStyles.centered]: isSeatSelectionUnavailable,
              [animatedContentStyles.fullHeight]: !shouldRenderInline,
              [animatedContentStyles.minHeight]: shouldRenderInline,
              [animatedContentStyles.scrollable]: !shouldRenderInline,
              [animatedContentStyles.pageBackground]: !shouldRenderInline || isSeatSelectionUnavailable,
              [animatedContentStyles.surfaceBackground]: shouldRenderInline && !isSeatSelectionUnavailable
            }),
            custom: resolvedSlideDirection,
            exit: "exit",
            initial: "enter",
            transition: {
              duration: 0.2,
              ease: 'easeInOut'
            },
            variants: slideVariants,
            children: [showSeatingLightOrBeside && /*#__PURE__*/_jsx(SeatingLightOrBeside, {
              activeSegment: activeSegment,
              carrier: currentSegment === null || currentSegment === void 0 ? void 0 : currentSegment.carrier,
              onChangeSeatingLightBeside: onChangeSeatingLightBeside,
              price: price,
              selectedSeatPreference: getSelected(activeSegment),
              typename: __typename
            }), showUnavailableSegment && /*#__PURE__*/_jsx("div", {
              "data-testid": "unavailable-segment",
              children: /*#__PURE__*/_jsx(SegmentPanel, {
                content: t('Product.SeatMap.Segment.Unavailable.Content'),
                icons: [[{
                  id: 'c_flat-seat-selected-blue',
                  size: '32px'
                }, {
                  id: 'ban',
                  width: '31px',
                  height: '19px',
                  top: '4px'
                }]],
                title: t('Product.SeatMap.Segment.Unavailable.Title')
              })
            }), /*#__PURE__*/_jsxs(Inline, {
              align: "center",
              noWrap: true,
              children: [showSeatPickerSidebar && /*#__PURE__*/_jsx(SeatMapTravelers, {
                activeSeatAssignments: getSelected(activeSegment),
                activeSegment: activeSegment,
                activeTraveler: activeTraveler,
                isRyanairInTrip: isRyanairInTrip,
                onChange: handleChangeTraveler,
                onRemoveSeat: onRemove,
                seatTravelers: getSegmentTravelers(travelers, flattenedBounds, activeSegment),
                setTravelerButtonWithRemoveLink: setTravelerButtonWithRemoveLink,
                shouldRenderInline: shouldRenderInline,
                travelerButtonWithRemoveLink: travelerButtonWithRemoveLink
              }), isSummaryShowing && /*#__PURE__*/_jsx(SeatSummary, {
                isRyanairInTrip: isRyanairInTrip,
                onRemoveSeat: onRemove,
                onToggleSeatSelect: handleSelectSeatFromSummary,
                segments: flattenedBounds,
                selectedSeats: selectedSeatsBySegment,
                shouldRenderInline: shouldRenderInline,
                totalMarkup: totalMarkup,
                totalPrice: totalPrice,
                travelers: travelers
              }), showCabinSection && /*#__PURE__*/_jsx(SeatMapCabinSection, {
                activeDeck: activeDeck,
                activeSeatAssignments: getSelected(activeSegment),
                cabins: cabins,
                carrier: currentSegment === null || currentSegment === void 0 ? void 0 : currentSegment.carrier,
                carrierNameLowerCase: carrierNameLowerCase,
                deckOptions: deckOptions,
                isRyanairInTrip: isRyanairInTrip,
                onChange: handleChangeSeat,
                onDeckChange: handleDeckChange,
                onSkipButtonFocus: setIsSkipButtonFocused,
                onSkipButtonHover: setIsSkipButtonHovered,
                seatTravelers: getSegmentTravelers(travelers, flattenedBounds, activeSegment)
              }), showSeatPickerSidebar && /*#__PURE__*/_jsx(SeatCharacteristics, {
                expandedCharacteristicKey: expandedCharacteristicKey,
                onToggle: handleToggle,
                seatCharacteristics: seatCharacteristics,
                shouldRenderInline: shouldRenderInline
              })]
            })]
          }, isSummaryShowing ? 'summary' : activeSegment)
        }), /*#__PURE__*/_jsx(SeatMapFooter, {
          activeSegment: activeSegment,
          canTriggerUnbookedSegmentsDialog: canTriggerUnbookedSegmentsDialog,
          isFinalSelectionStep: isFinalSelectionStep,
          isFirstSegment: isFirstSegment,
          isPostBooking: isPostBooking,
          isSkipButtonShown: isSkipButtonFocused || isSkipButtonHovered,
          isUnbookedSegmentsDialogOpen: isUnbookedSegmentsDialogOpen,
          onClose: handleOnClose,
          onPreviousSegmentClick: handlePreviousSegment,
          onSaveAndContinue: handleNextSegment,
          onSeatConfirmation: handleOnSeatConfirmation,
          segments: flattenedBounds,
          selectedSeats: selectedSeats,
          selectNoOption: handleOnOptOut,
          shouldRenderInline: shouldRenderInline,
          shouldShowSeatCharacteristicsButton: shouldShowSeatCharacteristicsButton,
          shouldShowSummary: isSummaryShowing,
          totalPrice: totalPrice
        })]
      })
    }), /*#__PURE__*/_jsx(SeatMapPersistDialog, {
      isOpen: isPersistDialogOpen,
      onSelection: handleConfirmationSelection,
      segmentIndex: firstUnbookedSegmentIndex
    }), /*#__PURE__*/_jsx(SeatMapUnbookedSegmentsDialog, {
      dismissText: unbookedSegmentsDismissText,
      isOpen: isUnbookedSegmentsDialogOpen,
      onSelection: handleConfirmationSelection,
      segments: nonSelectedAvailableSegments
    })]
  });
};
export default SeatMap;
//# sourceMappingURL=SeatMap.js.map